/**
 * webapp: strip known tracking parameters from http(s) links.
 *
 * Allowlist only — never "drop all query params". `t=` on YouTube is a
 * timestamp, `v=` is the video id; blanket stripping would break links.
 */

// Stripped on any host.
const GLOBAL_EXACT = new Set([
  'fbclid',
  'gclid',
  'dclid',
  'gbraid',
  'wbraid',
  'msclkid',
  'mc_eid',
  'igshid',
])

// host === domain || host.endsWith('.' + domain)
const PER_DOMAIN: Record<string, string[]> = {
  'youtube.com': ['si', 'pp', 'feature'],
  'youtu.be': ['si', 'pp', 'feature'],
  'instagram.com': ['igsh'],
  'x.com': ['s', 't', 'ref_src', 'ref_url'],
  'twitter.com': ['s', 't', 'ref_src', 'ref_url'],
  'open.spotify.com': ['si'],
  'tiktok.com': ['_t', '_r'],
}

// amazon.* (any TLD) — keep `th` and other functional params.
const AMAZON_EXACT = new Set([
  'tag',
  'linkCode',
  'ref',
  'ref_',
  'qid',
  'sr',
  'sprefix',
  'crid',
  'dib',
  'dib_tag',
])

/**
 * Returns the cleaned URL, or `null` when nothing was stripped or the input
 * is not a parseable http(s) URL.
 */
export function stripTrackingParams(url: string): string | null {
  let u: URL
  try {
    u = new URL(url)
  } catch {
    return null
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') {
    return null
  }

  const host = u.hostname.toLowerCase()
  // ponytail: prefix match, so amazon.evil.com is also treated as amazon.
  // Harmless (we only remove tracker params); tighten with a PSL if it matters.
  const isAmazon = /(^|\.)amazon\.[a-z.]+$/.test(host)

  const perDomain = new Set<string>()
  for (const domain of Object.keys(PER_DOMAIN)) {
    if (host === domain || host.endsWith('.' + domain)) {
      for (const p of PER_DOMAIN[domain] ?? []) perDomain.add(p)
    }
  }

  const shouldStrip = (key: string): boolean => {
    if (key.startsWith('utm_')) return true
    if (GLOBAL_EXACT.has(key)) return true
    if (perDomain.has(key)) return true
    if (isAmazon) {
      if (AMAZON_EXACT.has(key)) return true
      if (key.startsWith('pd_rd_') || key.startsWith('pf_rd_')) return true
    }
    return false
  }

  let changed = false

  // Raw string surgery keeps the untouched params byte-for-byte (order and
  // percent-encoding), which re-serializing via URLSearchParams would not.
  // Tracker param names are plain ASCII, so the raw key needs no decoding.
  const rawQuery = u.search.slice(1)
  if (rawQuery) {
    const pairs = rawQuery.split('&')
    const kept = pairs.filter(p => !shouldStrip(p.split('=')[0] ?? ''))
    if (kept.length !== pairs.length) {
      changed = true
      u.search = kept.join('&')
    }
  }

  if (isAmazon) {
    const trimmed = u.pathname.replace(/\/ref=[^/]*$/, '')
    if (trimmed !== u.pathname) {
      u.pathname = trimmed
      changed = true
    }
  }

  return changed ? u.href : null
}

// Coarse http(s) token; trailing prose punctuation is trimmed off the match.
const URL_TOKEN = /https?:\/\/\S+/gi

/**
 * Clean tracking params out of a pasted plain-text block (composer paste chip).
 * Returns the rewritten text plus the url pair (for undo), or `null` when there
 * is nothing to strip or the paste is ambiguous.
 *
 * Deliberately single-URL: a paste with zero or several URLs is left untouched
 * so we never rewrite text we're unsure about and undo stays one unambiguous
 * swap.
 * ponytail: the trailing-punctuation trim can mis-clip a URL that genuinely
 * ends in `)`/`.` (rare — some Wikipedia links); it only affects the stripped
 * URL, never the surrounding text, which is preserved byte-for-byte. Upgrade to
 * a real URL tokenizer if such links turn up carrying tracking params.
 */
export function stripTrackingFromPaste(
  text: string
): { cleaned: string; originalUrl: string; cleanedUrl: string } | null {
  const urls = text.match(URL_TOKEN)
  if (!urls || urls.length !== 1) return null
  const originalUrl = urls[0].replace(/[.,;:!?)\]}>"']+$/, '')
  const cleanedUrl = stripTrackingParams(originalUrl)
  if (cleanedUrl == null) return null
  // replacer fn, not string: '$' is a legal URL char and would trigger $-substitution
  return { cleaned: text.replace(originalUrl, () => cleanedUrl), originalUrl, cleanedUrl }
}
